import { Address, ChainConfiguration, Wei } from '../../utils/types'
import { maxSwapSlippage, protocolPrecision } from '../../config'
import { ERC20, Pool } from '../../abi/abi'
import { fromWei, toWei } from '../../utils/static-web3'
import Web3 from 'web3'
import { Token } from '../token/token'

export interface SwapElements {}

interface ISwap {
  inAmountWei: Wei | undefined
  expectedOutAmountWei: Wei | undefined
  rate: number
  slippage: number
  pool: Address
  tokenIn: Token
  tokenOut: Token
  actionTreatmentNonce: number
}

export interface ValidSwap extends ISwap {
  inAmountWei: Wei
  expectedOutAmountWei: Wei
}

export interface SuccessSwap extends ValidSwap {
  transactionHash: string
  realOutAmountWei: Wei
}

/**
 * web3 is used only for utils function. Should not be used for
 */
export class Swap implements ISwap {
  // A swap can occur depending on token in amount or desired token out
  private inAmount: number | undefined
  inAmountWei: Wei | undefined
  expectedOutAmountWei: Wei | undefined = undefined
  private expectedOutAmount: number | undefined
  rate: number = 0
  slippage: number

  // set after transaction
  transactionHash: string | undefined
  realOutAmountWei: Wei | undefined

  constructor(
    public config: ChainConfiguration,
    public pool: Address,
    public tokenIn: Token,
    public tokenOut: Token,
    public actionTreatmentNonce: number
  ) {
    const slippage = window.localStorage.getItem('slippage')
    if (slippage) {
      this.slippage = parseFloat(slippage)
    } else {
      this.slippage = maxSwapSlippage
    }
  }

  // TODO: not well typed. Will be spread all over the app
  serialize() {
    return {
      tokenIn: this.tokenIn,
      tokenOut: this.tokenOut,
      pool: this.pool,
      inAmountWei: this.inAmountWei,
      expectedOutAmountWei: this.expectedOutAmountWei,
      realOutAmountWei: this.realOutAmountWei,
      transactionHash: this.transactionHash,
      explorerUrl: this.getExplorerUrl(),
    }
  }

  setInAmountWei(weiAmount: Wei) {
    this.inAmountWei = weiAmount
    this.inAmount = fromWei(weiAmount, this.tokenIn.decimals)
  }

  setExpectedOutAmount(amount: Wei | undefined) {
    this.expectedOutAmountWei = amount
    this.expectedOutAmount = amount
      ? fromWei(amount, this.tokenOut.decimals)
      : undefined
  }

  setRealAmountWei(amount: Wei | undefined) {
    this.realOutAmountWei = amount
  }

  setTransactionHash(hash: string) {
    this.transactionHash = hash
  }

  setPool(pool: Address) {
    this.pool = pool
    // @ts-ignore looks working...
    this.poolContract = new web3.eth.Contract(Pool, this.pool)
  }

  calculateMinAmountWei(): Wei {
    if (!this.inAmount) {
      throw 'inAmount expected'
    }
    if (!this.expectedOutAmount) {
      throw 'expectedOutAmountWei expected'
    }
    return toWei(
      this.expectedOutAmount / (1 + this.slippage),
      this.tokenOut.decimals
    )
  }

  async getRate() {}

  getMaxPrice(): Wei {
    if (!this.inAmountWei) {
      throw 'inAmount expected'
    }
    if (!this.expectedOutAmountWei) {
      throw 'expectedOutAmount expected'
    }

    // Protocol's equations use wei amounts (ie, humanAmount * 10**decimals),
    // so any differences in decimals will be reflected in the price.
    // eg: at equilibrium, the price of USDC (6 decimals) in DAI (18decimals) will be 1e12
    const price =
      parseFloat(this.inAmountWei) / parseFloat(this.expectedOutAmountWei)
    // Then, the protocol's fixed point arithmetics use $protocolPrecision decimals
    return toWei(price * (1 + this.slippage), protocolPrecision)
  }

  isValidWithTokenInBeforeOutEstimation(): boolean {
    if (!this.inAmount || !this.inAmountWei) {
      return false
    }
    if (!(this.inAmount > 0)) {
      return false
    }
    return true
  }

  isValidWithTokenInBeforeSwap(balanceIn: Wei): boolean {
    if (!this.isValidWithTokenInBeforeOutEstimation()) {
      return false
    }
    if (!this.inAmountWei || !this.expectedOutAmount) {
      return false
    }
    if (!this.expectedOutAmountWei || !this.expectedOutAmount) {
      return false
    }

    if (!(this.expectedOutAmount > 0)) {
      return false
    }
    const amount = fromWei(this.inAmountWei, this.tokenIn.decimals)
    if (fromWei(balanceIn, this.tokenIn.decimals) < amount) {
      return false
    }
    return amount > 0
  }

  getExplorerUrl() {
    return this.transactionHash
      ? this.config.getExplorerUrl(this.transactionHash)
      : ''
  }
}
